"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
const steps = [{
  number: "01",
  title: "Application",
  description: "Submit your business profile and trade requirements. We will provide a tailored partnership proposal and a clear checklist of necessary documentation.",
  duration: "< 1 hr"
}, {
  number: "02",
  title: "Verification",
  description: "Our compliance team validates your trade history and operational standards with complete confidentiality.  Compliance Verified.  Secure Payment Gateway. No hidden fees, no surprises.",
  duration: "24-48 hrs"
}, {
  number: "03",
  title: "Agreement",
  description: "We issue a formal partnership agreement outlining service terms, pricing, and operational protocols. Signatures finalize your account setup.",
  duration: "24 hrs"
}, {
  number: "04",
  title: "Activation",
  description: "Your account is fully activated. You gain immediate access to our trading platform and dedicated support team for your first transaction.",
  duration: "< 24 hrs"
}];
export default function Onboarding() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [lineDrawn, setLineDrawn] = useState(false);
  useEffect(() => {
    if (isVisible) {
      const t = setTimeout(() => setLineDrawn(true), 100);
      return () => clearTimeout(t);
    }
  }, [isVisible]);
  return <section data-section-id="15920" id="partnershiponboardingprocess" className="bg-white py-24 md:py-32">
      <div ref={ref} className="mx-auto max-w-[1240px] px-5 md:px-14">
        <div className="mx-auto max-w-3xl text-center">
          <div className="h-px w-full bg-[#e0e0e0]" />
          <p className="mt-8 text-xs font-medium tracking-[0.14em] uppercase text-[#1a3a52] font-sans">
            Our Process
          </p>
          <h2 className="mt-6 font-sans font-semibold text-[#1a1a1a]" style={{
          fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.1
        }}>
            Four steps.{" "}
            <span className="italic relative inline-block" style={{
            fontFamily: "var(--font-sans)"
          }}>
              Three
              <span className="absolute left-0 right-0 block" style={{
              bottom: "-4px",
              height: "1px",
              backgroundColor: "#d4a574"
            }} />
            </span>{" "}
            business days.
          </h2>
          <p className="mx-auto mt-6 text-[#1a1a1a] font-sans" style={{
          fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
          lineHeight: 1.6,
          maxWidth: "52ch"
        }}>
            From initial inquiry to full platform access — a streamlined, professional onboarding experience designed for partners who value efficiency and clarity.
          </p>
          <p className="mx-auto mt-4 text-[#1a1a1a]/80 font-sans" style={{
          fontSize: "1rem",
          lineHeight: 1.6,
          maxWidth: "65ch"
        }}>
            KTEX maintains an industry-leading reputation through rigorous KYC and background checks, ensuring full compliance with international trade standards. As a DAFZA-registered entity, we provide secure payment guarantees and transparent dispute resolution procedures, reflecting our deep commitment to long-term partnership success.
          </p>
        </div>

        <div className="hidden md:block mt-20">
          <div className="relative grid grid-cols-4 gap-8">
            <div className="absolute left-0 right-0 pointer-events-none" style={{
            top: "50%",
            height: "1px",
            transform: "translateY(-0.5px)"
          }}>
              <div className="h-full origin-left" style={{
              backgroundColor: "#d4a574",
              transform: lineDrawn ? "scaleX(1)" : "scaleX(0)",
              transition: "transform 900ms cubic-bezier(0.2, 0.7, 0.2, 1)"
            }} />
            </div>

            {steps.map((step, i) => <div data-index={i} key={step.number} className="relative flex flex-col items-center text-center group cursor-default" onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
                <div className="pb-12">
                  <p className="font-mono text-[#1a3a52]" style={{
                fontSize: "0.8125rem",
                letterSpacing: "0.02em"
              }}>
                    {step.number}
                  </p>
                  <div className="mt-3 flex flex-col items-center gap-2">
                    <h3 className="font-sans font-medium text-[#1a1a1a] relative inline-block" style={{
                  fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.25
                }}>
                      {step.title}
                      <span className="absolute left-0 block" style={{
                    bottom: "-4px",
                    height: "1px",
                    backgroundColor: "#d4a574",
                    width: hoveredIndex === i ? "100%" : "0%",
                    transition: "width 220ms cubic-bezier(0.2, 0.7, 0.2, 1)"
                  }} />
                    </h3>
                    {i === 1 && <Badge variant="outline" className="border-[#d4a574] text-[#d4a574] font-sans text-[10px] uppercase tracking-wider rounded-lg">
                         Compliance Verified
                      </Badge>}
                  </div>
                </div>

                <div className="relative z-10 rounded-lg" style={{
              backgroundColor: "#d4a574",
              width: hoveredIndex === i ? "12px" : "8px",
              height: hoveredIndex === i ? "12px" : "8px",
              transform: lineDrawn ? "scale(1)" : "scale(0.6)",
              opacity: lineDrawn ? 1 : 0,
              transition: `transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1) ${200 + i * 180}ms, opacity 220ms ease-out ${200 + i * 180}ms, width 220ms ease, height 220ms ease`
            }} />

                <div className="pt-12">
                  <p className="text-[#1a1a1a] font-sans" style={{
                fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                lineHeight: 1.62
              }}>
                    {step.description}
                  </p>
                  <p className="mt-5 font-mono text-[#1a3a52]" style={{
                fontSize: "0.8125rem",
                letterSpacing: "0.02em"
              }}>
                    {step.duration}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        <div className="md:hidden mt-16 relative">
          <div className="absolute top-0 bottom-0 pointer-events-none" style={{
          left: "7px",
          width: "1px"
        }}>
            <div className="w-full origin-top" style={{
            backgroundColor: "#d4a574",
            height: "100%",
            transform: lineDrawn ? "scaleY(1)" : "scaleY(0)",
            transition: "transform 900ms cubic-bezier(0.2, 0.7, 0.2, 1)"
          }} />
          </div>

          <div className="space-y-12">
            {steps.map((step, i) => <div data-index={i} key={step.number} className="relative pl-10">
                <div className="absolute rounded-lg" style={{
              left: "3px",
              top: "8px",
              width: "8px",
              height: "8px",
              backgroundColor: "#d4a574",
              transform: lineDrawn ? "scale(1)" : "scale(0.6)",
              opacity: lineDrawn ? 1 : 0,
              transition: `transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1) ${200 + i * 180}ms, opacity 220ms ease-out ${200 + i * 180}ms`
            }} />

                <p className="font-mono text-[#1a3a52]" style={{
              fontSize: "0.8125rem",
              letterSpacing: "0.02em"
            }}>
                  {step.number}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <h3 className="font-sans font-medium text-[#1a1a1a]" style={{
                fontSize: "1.25rem",
                letterSpacing: "-0.01em",
                lineHeight: 1.25
              }}>
                    {step.title}
                  </h3>
                  {i === 1 && <Badge variant="outline" className="border-[#d4a574] text-[#d4a574] font-sans text-[10px] uppercase tracking-wider rounded-lg">
                       Secure Payment Gateway
                    </Badge>}
                </div>
                <p className="mt-3 text-[#1a1a1a] font-sans" style={{
              fontSize: "0.9375rem",
              lineHeight: 1.62
            }}>
                  {step.description}
                </p>
                <p className="mt-4 font-mono text-[#1a3a52]" style={{
              fontSize: "0.8125rem",
              letterSpacing: "0.02em"
            }}>
                  {step.duration}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}